import { createContext, useCallback, useContext, useMemo, useReducer } from 'react';

import { SnackbarItem, SnackbarContainer } from '../components';

import type { SnackbarProviderData, SnackbarProviderProps, SnackbarState } from './types';
import { snackbarReducer } from './utils';

const SnackbarContext = createContext<SnackbarProviderData | null>(null);

function SnackbarProvider(props: Readonly<SnackbarProviderProps>) {
  const { children, options } = props;
  const {
    autoHideDuration = 3000,
    snackbarLimit = 5,
    showLatestOnTop = true,
    snackbarVariant: {
      default: defaultSnackbarVariant = 'info',
      pending: pendingSnackbarVariant = 'info',
    } = {},
  } = options || {};
  const [snackbarList, dispatch] = useReducer(snackbarReducer, []);

  const addNewSnackbar = useCallback(
    (snackbar: SnackbarState) => {
      dispatch({
        type: 'ADD_SNACKBAR',
        payload: {
          snackbar,
          options: {
            showLatestOnTop,
            snackbarLimit,
          },
        },
      });
    },
    [showLatestOnTop, snackbarLimit, dispatch],
  );

  const closeSnackbar = useCallback(
    (id: string) => {
      dispatch({
        type: 'REMOVE_SNACKBAR',
        payload: {
          id,
        },
      });
    },
    [dispatch],
  );

  const showSnackbar: SnackbarProviderData['showSnackbar'] = useCallback(
    (args) => {
      if (snackbarList.length >= snackbarLimit) {
        console.warn(
          'Snackbar limit reached. Please close some notifications before adding new ones.',
        );
        return;
      }

      const { variant = defaultSnackbarVariant, message, ...restArgs } = args;
      const id = new Date().getTime().toString();
      addNewSnackbar({
        id,
        message,
        variant,
        pending: false,
        ...restArgs,
      });
    },
    [snackbarList, snackbarLimit, addNewSnackbar, dispatch],
  );

  const showPromiseSnackbar: SnackbarProviderData['showPromiseSnackbar'] = useCallback(
    (args) => {
      if (snackbarList.length >= snackbarLimit) {
        console.warn(
          'Snackbar limit reached. Please close some notifications before adding new ones.',
        );
        return;
      }

      const { message, promise, ...restArgs } = args;
      const id = new Date().getTime().toString();

      addNewSnackbar({
        id,
        message: message.pending,
        variant: pendingSnackbarVariant,
        pending: true,
        ...restArgs,
      });

      promise()
        .then(() => {
          dispatch({
            type: 'UPDATE_SNACKBAR',
            payload: {
              id,
              message: message.success,
              variant: 'success',
              pending: false,
            },
          });
        })
        .catch((error: unknown) => {
          dispatch({
            type: 'UPDATE_SNACKBAR',
            payload: {
              id,
              message: typeof message.error === 'function' ? message.error(error) : message.error,
              variant: 'error',
              pending: false,
            },
          });
        });
    },
    [snackbarList, snackbarLimit, addNewSnackbar, dispatch],
  );

  const contextValue = useMemo(
    () => ({ showSnackbar, showPromiseSnackbar }),
    [showSnackbar, showPromiseSnackbar],
  );

  return (
    <SnackbarContext.Provider value={contextValue}>
      {children}
      <SnackbarContainer>
        {snackbarList.map((snackbar) => {
          const isOpen = snackbarList.includes(snackbar);

          const snackbarAutoHideDuration = snackbar.pending
            ? null
            : (snackbar.autoHideDuration ?? autoHideDuration);

          return (
            <SnackbarItem
              key={snackbar.id}
              message={snackbar.message ?? ''}
              variant={snackbar.variant}
              loading={snackbar.pending}
              snack={{
                id: snackbar.id,
                open: isOpen,
                autoHideDuration: snackbarAutoHideDuration,
                onClose: () => closeSnackbar(snackbar.id),
                sx: (theme) => ({
                  pt: theme.spacing(2),
                }),
              }}
            />
          );
        })}
      </SnackbarContainer>
    </SnackbarContext.Provider>
  );
}

function useSnackbar() {
  const context = useContext(SnackbarContext);

  if (!context) {
    throw new Error('useSnackbar must be used within a SnackbarProvider');
  }

  return context;
}

export { SnackbarContext, SnackbarProvider, useSnackbar };
